<script lang="ts">
export default {
  name: 'Reporting',
};
</script>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { ServerIcon, AlertCircleIcon, FileTextIcon, RefreshCwIcon, FilterIcon, ShieldIcon } from 'lucide-vue-next';
import { TabsContent, TabsList, TabsRoot, TabsTrigger } from 'reka-ui';
import Button from '@/components/ui/UIButton.vue';
import Combobox, { type ComboboxOption } from '@/components/v2/Combobox.vue';
import { adminService, type LambdaFunction, type LambdaLog, type LambdaLogsParams, type BeanstalkEnvironment, type BeanstalkLog, type BeanstalkLogsParams, type DlpAuditLog, type DlpAuditParams } from '@/services/AdminService';
import userService from '@/services/UserService';

const currentTab = ref<string>('beanstalk');

// -----
// data
// -----

const loading = ref(true);
const error = ref<string | null>(null);
const lambdaFunctions = ref<LambdaFunction[]>([]);

// Beanstalk logs state
const selectedBeanstalkEnvironment = ref<string>('');
const loadedBeanstalkEnvironment = ref<string>(''); // Track which environment the current logs are from
const beanstalkEnvironments = ref<BeanstalkEnvironment[]>([]);
const beanstalkLogs = ref<BeanstalkLog[]>([]);
const beanstalkLogsLoading = ref(false);
const beanstalkLogsError = ref<string | null>(null);
const beanstalkLogsCount = ref(0);

// Beanstalk filter state
const beanstalkLogLevel = ref<string>('');
const beanstalkCorrelationId = ref<string>('');
const beanstalkUserId = ref<string>('');
const beanstalkLimit = ref<number>(100);

// User options for combobox
const userOptions = ref<ComboboxOption[]>([]);
const userIdToNameMap = ref<Map<number, string>>(new Map());
const beanstalkStartTime = ref<string>('');
const beanstalkEndTime = ref<string>('');

// Lambda logs state
const selectedLambdaFunction = ref<string>('');
const loadedLambdaFunction = ref<string>(''); // Track which function the current logs are from
const lambdaLogs = ref<LambdaLog[]>([]);
const lambdaLogsLoading = ref(false);
const lambdaLogsError = ref<string | null>(null);
const lambdaLogsCount = ref(0);

// Lambda filter state
const lambdaLogLevel = ref<string>('');
const lambdaLimit = ref<number>(100);
const lambdaStartTime = ref<string>('');
const lambdaEndTime = ref<string>('');

// DLP audit logs state
const dlpLogs = ref<DlpAuditLog[]>([]);
const dlpLogsLoading = ref(false);
const dlpLogsError = ref<string | null>(null);

// DLP filter state
const dlpUserId = ref<string>('');
const dlpStartDate = ref<string>('');
const dlpEndDate = ref<string>('');

// -----
// lifecycle
// -----

async function populateUsers() {
  const users = await userService.getAllUsers();
  userOptions.value = users
    .filter(user => user.name || user.userEmail)
    .map(user => ({
      label: `${user.name || user.userEmail} (UserID: ${user.id})`,
      value: String(user.id),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
  
  // Build lookup map for displaying user names in logs
  users.forEach(user => {
    userIdToNameMap.value.set(user.id, user.name || user.userEmail);
  });
}

onMounted(async () => {
  // Set default time ranges
  setLambdaDefaultTimeRange();
  setBeanstalkDefaultTimeRange();
  setDlpDefaultTimeRange();
  
  try {
    const [lambdaResponse, beanstalkResponse] = await Promise.all([
      adminService.getLambdaFunctions(),
      adminService.getBeanstalkEnvironments(),
      populateUsers(),
    ]);
    
    lambdaFunctions.value = lambdaResponse.functions;
    beanstalkEnvironments.value = beanstalkResponse.environments;
  } catch (err) {
    console.error('Failed to load data:', err);
    error.value = 'Failed to load Lambda functions and Beanstalk environments. Please check your permissions and try again.';
  } finally {
    loading.value = false;
  }
});

// -----
// methods
// -----

const loadBeanstalkLogs = async () => {
  if (!selectedBeanstalkEnvironment.value) {
    beanstalkLogsError.value = 'Please select a Beanstalk environment first.';
    return;
  }

  beanstalkLogsLoading.value = true;
  beanstalkLogsError.value = null;

  try {
    const params: BeanstalkLogsParams = {
      ebEnvironment: selectedBeanstalkEnvironment.value,
      limit: beanstalkLimit.value,
      // Convert form values to ISO format
      startTime: new Date(beanstalkStartTime.value).toISOString(),
      endTime: new Date(beanstalkEndTime.value).toISOString(),
    };

    if (beanstalkLogLevel.value) {
      params.logLevel = beanstalkLogLevel.value as 'ERROR' | 'WARN' | 'INFO' | 'DEBUG';
    }
    if (beanstalkCorrelationId.value) {
      params.correlationId = beanstalkCorrelationId.value;
    }
    if (beanstalkUserId.value) {
      params.userId = parseInt(beanstalkUserId.value, 10);
    }

    const response = await adminService.getBeanstalkLogs(params);
    beanstalkLogs.value = response.logs;
    beanstalkLogsCount.value = response.count;
    loadedBeanstalkEnvironment.value = selectedBeanstalkEnvironment.value;
  } catch (err: any) {
    console.error('Failed to load Beanstalk logs:', err);
    
    // Handle specific error messages from the backend
    if (err.response?.data?.error) {
      beanstalkLogsError.value = err.response.data.error;
    } else if (err.message && err.message.includes('does not exist')) {
      beanstalkLogsError.value = `Log group for environment "${selectedBeanstalkEnvironment.value}" does not exist. This environment may not have logging configured in CloudWatch.`;
    } else {
      beanstalkLogsError.value = 'Failed to load Beanstalk logs. Please check your permissions and try again.';
    }
  } finally {
    beanstalkLogsLoading.value = false;
  }
};

const clearBeanstalkFilters = () => {
  beanstalkLogLevel.value = '';
  beanstalkCorrelationId.value = '';
  beanstalkUserId.value = '';
  beanstalkLimit.value = 100;
  // Reset to default 24-hour time range instead of clearing
  setBeanstalkDefaultTimeRange();
};

// Format dates for datetime-local input (YYYY-MM-DDTHH:MM)
const formatForDatetimeLocal = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const setLambdaDefaultTimeRange = () => {
  const now = new Date();
  const twentyFourHoursAgo = new Date(now.getTime() - (24 * 60 * 60 * 1000));
  
  lambdaStartTime.value = formatForDatetimeLocal(twentyFourHoursAgo);
  lambdaEndTime.value = formatForDatetimeLocal(now);
};

const setBeanstalkDefaultTimeRange = () => {
  const now = new Date();
  const twentyFourHoursAgo = new Date(now.getTime() - (24 * 60 * 60 * 1000));

  beanstalkStartTime.value = formatForDatetimeLocal(twentyFourHoursAgo);
  beanstalkEndTime.value = formatForDatetimeLocal(now);
};

const loadLambdaLogs = async () => {
  if (!selectedLambdaFunction.value) {
    lambdaLogsError.value = 'Please select a Lambda function first.';
    return;
  }

  lambdaLogsLoading.value = true;
  lambdaLogsError.value = null;

  try {
    const params: LambdaLogsParams = {
      function: selectedLambdaFunction.value,
      limit: lambdaLimit.value,
      // Convert form values to ISO format
      startTime: new Date(lambdaStartTime.value).toISOString(),
      endTime: new Date(lambdaEndTime.value).toISOString(),
    };

    if (lambdaLogLevel.value) {
      params.logLevel = lambdaLogLevel.value as 'ERROR' | 'WARN' | 'INFO' | 'DEBUG';
    }

    const response = await adminService.getLambdaLogs(params);
    lambdaLogs.value = response.logs;
    lambdaLogsCount.value = response.count;
    loadedLambdaFunction.value = selectedLambdaFunction.value;
  } catch (err) {
    console.error('Failed to load Lambda logs:', err);
    lambdaLogsError.value = 'Failed to load Lambda logs. Please check your permissions and try again.';
  } finally {
    lambdaLogsLoading.value = false;
  }
};

const clearLambdaFilters = () => {
  lambdaLogLevel.value = '';
  lambdaLimit.value = 100;
  // Reset to default 24-hour time range instead of clearing
  setLambdaDefaultTimeRange();
};

const setDlpDefaultTimeRange = () => {
  const now = new Date();
  const thirtyDaysAgo = new Date(now.getTime() - (30 * 24 * 60 * 60 * 1000));

  dlpStartDate.value = formatForDatetimeLocal(thirtyDaysAgo);
  dlpEndDate.value = formatForDatetimeLocal(now);
};

const loadDlpLogs = async () => {
  dlpLogsLoading.value = true;
  dlpLogsError.value = null;

  try {
    const params: DlpAuditParams = {
      startDate: new Date(dlpStartDate.value).toISOString(),
      endDate: new Date(dlpEndDate.value).toISOString(),
      categoryID: 1,
    };

    if (dlpUserId.value) {
      params.userId = parseInt(dlpUserId.value, 10);
    }

    dlpLogs.value = await adminService.getDlpAuditLogs(params);
  } catch (err: any) {
    console.error('Failed to load DLP audit logs:', err);

    if (err.response?.data?.error) {
      dlpLogsError.value = `${err.response.data.error}${err.response.data.details ? ': ' + err.response.data.details : ''}`;
    } else {
      dlpLogsError.value = 'Failed to load DLP audit logs. Please check your permissions and try again.';
    }
  } finally {
    dlpLogsLoading.value = false;
  }
};

const clearDlpFilters = () => {
  dlpUserId.value = '';
  setDlpDefaultTimeRange();
};

const formatTimestamp = (timestamp: string) => {
  return new Date(timestamp).toLocaleString();
};

const getLogLevelColor = (level: string) => {
  if (!level) {
    return 'tw:text-gray-600 tw:bg-gray-50 tw:border-gray-200';
  }
  
  switch (level.toLowerCase()) {
    case 'error':
      return 'tw:text-red-600 tw:bg-red-50 tw:border-red-200';
    case 'warn':
      return 'tw:text-yellow-600 tw:bg-yellow-50 tw:border-yellow-200';
    case 'info':
      return 'tw:text-blue-600 tw:bg-blue-50 tw:border-blue-200';
    case 'debug':
      return 'tw:text-gray-600 tw:bg-gray-50 tw:border-gray-200';
    default:
      return 'tw:text-gray-600 tw:bg-gray-50 tw:border-gray-200';
  }
};

const getUserDisplayName = (userId: number): string => {
  return userIdToNameMap.value.get(userId) || `User ${userId}`;
};

const queryParamLabels: Record<string, string> = {
  startDate: 'Start Date',
  endDate: 'End Date',
  coverageType: 'Coverage Type',
  topLevelIndustry: 'Top Level Industry',
  industry: 'Industry',
  wholesalerGroup: 'Wholesaler Group',
  carrier: 'Carrier',
  attachmentPointValueMin: 'Attachment Min',
  attachmentPointValueMax: 'Attachment Max',
  retentionMin: 'Retention Min',
  retentionMax: 'Retention Max',
  accountAttributes: 'Account Attributes',
  agency: 'Agency',
  showExternal: 'Show External',
  activeOnly: 'Active Only',
  ownershipType: 'Ownership Type',
  runoffFlag: 'Runoff',
  peFlag: 'Primary/Excess',
  daysUntilExpiration: 'Days Until Expiration',
  accountStates: 'Account States',
  departments: 'Departments',
  searchQuery: 'Search',
  searchField: 'Search Field',
  servicingRolesSearch: 'Servicing Roles Search',
  includeIndustryChildren: 'Include Industry Children',
};

// Params that are not user-facing filters
const ignoredQueryParams = new Set([
  'limit', 'offset', 'parentOnly', 'sortField', 'sortDirection', 'format',
]);

const filterCriteriaLabels: Record<string, string> = {
  dateRange: 'Date Range',
  coverageTypes: 'Coverage Type',
  wholesalerGroups: 'Wholesaler Group',
  carrierGroups: 'Carrier',
  industries: 'Industry',
  topLevelIndustries: 'Top Level Industry',
  sicIndustries: 'SIC Industry',
  accountAttributes: 'Account Attributes',
  agencies: 'Agency',
  departments: 'Departments',
  ownershipTypes: 'Ownership Type',
  accountStates: 'Account States',
  attachmentRange: 'Attachment Range',
  retentionRange: 'Retention Range',
};

const parseDlpFilters = (queryParams: string | null, metadata: string | null): { label: string; value: string }[] => {
  const results: { label: string; value: string }[] = [];

  // Parse filterCriteria from metadata
  if (metadata) {
    try {
      const parsed = JSON.parse(metadata);
      const criteria = parsed.filterCriteria;
      if (criteria && typeof criteria === 'object') {
        for (const [key, val] of Object.entries(criteria)) {
          if (val == null) continue;
          const label = filterCriteriaLabels[key] || key;
          const value = Array.isArray(val) ? val.join(', ') : String(val);
          if (value) results.push({ label, value });
        }
      }
    } catch {
      // ignore parse errors
    }
  }

  // Parse query params as fallback/supplement
  if (queryParams) {
    const params = new URLSearchParams(queryParams);
    const grouped = new Map<string, string[]>();
    const hasSearch = params.get('searchQuery');

    for (const [key, value] of params.entries()) {
      if (ignoredQueryParams.has(key) || !value) continue;
      if (key === 'searchField' && !hasSearch) continue;
      if (!grouped.has(key)) {
        grouped.set(key, []);
      }
      grouped.get(key)!.push(value);
    }

    const existingLabels = new Set(results.map(r => r.label));
    for (const [key, values] of grouped.entries()) {
      const label = queryParamLabels[key] || key;
      if (existingLabels.has(label)) continue;
      results.push({ label, value: values.join(', ') });
    }
  }

  return results;
};

const getDlpColumns = (metadata: string | null): string[] => {
  if (!metadata) return [];
  try {
    const parsed = JSON.parse(metadata);
    if (Array.isArray(parsed.columns) && parsed.columns.length > 0) {
      return parsed.columns;
    }
  } catch {
    // ignore parse errors
  }
  return [];
};

</script>

<template>
  <div class="tw:min-h-screen tw:bg-linear-to-br tw:from-slate-50 tw:to-blue-50 tw:p-6">
    <div class="tw:mx-auto tw:max-w-7xl tw:space-y-6">
      <!-- Page Header -->
      <div class="tw:space-y-4 tw:text-center">
        
        <h1 class="tw:bg-clip-text tw:text-3xl tw:font-bold">📊 Reporting</h1>
        <p class="tw:text-md tw:mx-auto tw:max-w-2xl tw:text-slate-600">
          View logs from aws cloudwatch
        </p>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="tw:flex tw:justify-center tw:items-center tw:py-12">
        <div class="tw:animate-spin tw:rounded-full tw:h-8 tw:w-8 tw:border-b-2 tw:border-blue-600"></div>
        <span class="tw:ml-3 tw:text-slate-600">Loading data...</span>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="tw:bg-red-50 tw:border tw:border-red-200 tw:rounded-md tw:p-4">
        <div class="tw:flex">
          <AlertCircleIcon class="tw:h-5 tw:w-5 tw:text-red-400 tw:mr-2" />
          <div class="tw:text-sm tw:text-red-800">
            {{ error }}
          </div>
        </div>
      </div>

      <!-- Tabs Navigation -->
      <TabsRoot v-else v-model="currentTab" class="tw:w-full">
        <TabsList
          class="tw:inline-flex tw:h-10 tw:items-center tw:justify-center tw:rounded-md tw:bg-slate-100 tw:p-1 tw:text-slate-600">
          <TabsTrigger
            value="beanstalk"
            class="tw:inline-flex tw:items-center tw:justify-center tw:whitespace-nowrap tw:rounded-sm tw:px-3 tw:py-1.5 tw:text-sm tw:font-medium tw:ring-offset-white tw:transition-all tw:focus-visible:outline-none tw:focus-visible:ring-2 tw:focus-visible:ring-slate-950 tw:focus-visible:ring-offset-2 tw:disabled:pointer-events-none tw:disabled:opacity-50 tw:data-[state=active]:bg-white tw:data-[state=active]:text-slate-950 tw:data-[state=active]:shadow-xs">
            <ServerIcon class="tw:h-4 tw:w-4 tw:mr-2" />
            Beanstalk Logs
          </TabsTrigger>
          <TabsTrigger
            value="lambda"
            class="tw:inline-flex tw:items-center tw:justify-center tw:whitespace-nowrap tw:rounded-sm tw:px-3 tw:py-1.5 tw:text-sm tw:font-medium tw:ring-offset-white tw:transition-all tw:focus-visible:outline-none tw:focus-visible:ring-2 tw:focus-visible:ring-slate-950 tw:focus-visible:ring-offset-2 tw:disabled:pointer-events-none tw:disabled:opacity-50 tw:data-[state=active]:bg-white tw:data-[state=active]:text-slate-950 tw:data-[state=active]:shadow-xs">
            <FileTextIcon class="tw:h-4 tw:w-4 tw:mr-2" />
            Lambda Logs
          </TabsTrigger>
          <TabsTrigger
            value="dlp"
            class="tw:inline-flex tw:items-center tw:justify-center tw:whitespace-nowrap tw:rounded-sm tw:px-3 tw:py-1.5 tw:text-sm tw:font-medium tw:ring-offset-white tw:transition-all tw:focus-visible:outline-none tw:focus-visible:ring-2 tw:focus-visible:ring-slate-950 tw:focus-visible:ring-offset-2 tw:disabled:pointer-events-none tw:disabled:opacity-50 tw:data-[state=active]:bg-white tw:data-[state=active]:text-slate-950 tw:data-[state=active]:shadow-xs">
            <ShieldIcon class="tw:h-4 tw:w-4 tw:mr-2" />
            Policy Explorer Downloads
          </TabsTrigger>
        </TabsList>

        <!-- Beanstalk Tab Content -->
        <TabsContent value="beanstalk" class="tw:mt-4">

        <!-- Beanstalk Logs Section -->
        <div class="tw:bg-white tw:rounded-xl tw:shadow-sm tw:border tw:border-slate-200">
          <div class="tw:px-6 tw:py-4 tw:border-b tw:border-slate-200">
            <div class="tw:flex tw:items-center tw:space-x-3">
              <ServerIcon class="tw:h-5 tw:w-5 tw:text-slate-600" />
              <h3 class="tw:text-lg tw:font-semibold tw:text-slate-900">Beanstalk Environment Logs</h3>
            </div>
          </div>

          <div class="tw:p-6">
            <!-- Environment Selection and Filters -->
            <div class="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:lg:grid-cols-3 tw:gap-4 tw:mb-6">
              <!-- Environment Selection -->
              <div>
                <label class="tw:block tw:text-sm tw:font-medium tw:text-slate-700 tw:mb-2">
                  Beanstalk Environment *
                </label>
                <select
                  v-model="selectedBeanstalkEnvironment"
                  class="tw:w-full tw:px-3 tw:py-2 tw:border tw:border-slate-300 tw:rounded-lg tw:shadow-sm tw:focus:outline-none tw:focus:ring-2 tw:focus:ring-blue-500 tw:focus:border-blue-500 tw:transition-colors">
                  <option value="">Select an environment</option>
                  <option v-for="env in beanstalkEnvironments" :key="env.name" :value="env.name">
                    {{ env.name }}
                  </option>
                </select>
              </div>

              <!-- Log Level Filter -->
              <div>
                <label class="tw:block tw:text-sm tw:font-medium tw:text-gray-700 tw:mb-2">
                  Log Level
                </label>
                <select
                  v-model="beanstalkLogLevel"
                  class="tw:w-full tw:px-3 tw:py-2 tw:border tw:border-gray-300 tw:rounded-md tw:shadow-sm tw:focus:outline-none tw:focus:ring-blue-500 tw:focus:border-blue-500">
                  <option value="">All levels</option>
                  <option value="ERROR">ERROR</option>
                  <option value="WARN">WARN</option>
                  <option value="INFO">INFO</option>
                  <option value="DEBUG">DEBUG</option>
                </select>
              </div>

              <!-- Limit -->
              <div>
                <label class="tw:block tw:text-sm tw:font-medium tw:text-gray-700 tw:mb-2">
                  Limit
                </label>
                <input
                  v-model.number="beanstalkLimit"
                  type="number"
                  min="1"
                  max="1000"
                  class="tw:w-full tw:px-3 tw:py-2 tw:border tw:border-gray-300 tw:rounded-md tw:shadow-sm tw:focus:outline-none tw:focus:ring-blue-500 tw:focus:border-blue-500"
                  placeholder="100" />
              </div>

              <!-- Start Time -->
              <div>
                <label class="tw:block tw:text-sm tw:font-medium tw:text-gray-700 tw:mb-2">
                  Start Time
                </label>
                <input
                  v-model="beanstalkStartTime"
                  type="datetime-local"
                  class="tw:w-full tw:px-3 tw:py-2 tw:border tw:border-gray-300 tw:rounded-md tw:shadow-sm tw:focus:outline-none tw:focus:ring-blue-500 tw:focus:border-blue-500" />
              </div>

              <!-- End Time -->
              <div>
                <label class="tw:block tw:text-sm tw:font-medium tw:text-gray-700 tw:mb-2">
                  End Time
                </label>
                <input
                  v-model="beanstalkEndTime"
                  type="datetime-local"
                  class="tw:w-full tw:px-3 tw:py-2 tw:border tw:border-gray-300 tw:rounded-md tw:shadow-sm tw:focus:outline-none tw:focus:ring-blue-500 tw:focus:border-blue-500" />
              </div>

              <!-- User -->
              <div>
                <label class="tw:block tw:text-sm tw:font-medium tw:text-gray-700 tw:mb-2">
                  User
                </label>
                <Combobox
                  v-model="beanstalkUserId"
                  :options="userOptions"
                  placeholder="Select a user" />
              </div>

              <!-- Correlation ID -->
              <div class="tw:lg:col-span-2">
                <label class="tw:block tw:text-sm tw:font-medium tw:text-gray-700 tw:mb-2">
                  Correlation ID
                </label>
                <input
                  v-model="beanstalkCorrelationId"
                  type="text"
                  class="tw:w-full tw:px-3 tw:py-2 tw:border tw:border-gray-300 tw:rounded-md tw:shadow-sm tw:focus:outline-none tw:focus:ring-blue-500 tw:focus:border-blue-500"
                  placeholder="Enter correlation ID" />
              </div>
            </div>


            <!-- Action Buttons -->
            <div class="tw:flex tw:gap-3 tw:mb-6">
              <Button
                @click="loadBeanstalkLogs"
                :disabled="!selectedBeanstalkEnvironment || beanstalkLogsLoading">
                <RefreshCwIcon v-if="beanstalkLogsLoading" class="tw:animate-spin tw:h-4 tw:w-4 tw:mr-2" />
                <FileTextIcon v-else class="tw:h-4 tw:w-4 tw:mr-2" />
                {{ beanstalkLogsLoading ? 'Loading...' : 'Load Logs' }}
              </Button>
              
              <button
                @click="clearBeanstalkFilters"
                class="tw:inline-flex tw:items-center tw:px-4 tw:py-2 tw:border tw:border-slate-300 tw:text-sm tw:font-medium tw:rounded-lg tw:text-slate-700 tw:bg-white tw:hover:bg-slate-50 tw:focus:outline-none tw:focus:ring-2 tw:focus:ring-offset-2 tw:focus:ring-blue-500 tw:transition-colors tw:shadow-sm">
                <FilterIcon class="tw:h-4 tw:w-4 tw:mr-2" />
                Clear Filters
              </button>
            </div>

            <!-- Beanstalk Logs Error -->
            <div v-if="beanstalkLogsError" class="tw:bg-red-50 tw:border tw:border-red-200 tw:rounded-md tw:p-4 tw:mb-6">
              <div class="tw:flex">
                <AlertCircleIcon class="tw:h-5 tw:w-5 tw:text-red-400 tw:mr-2" />
                <div class="tw:text-sm tw:text-red-800">
                  {{ beanstalkLogsError }}
                </div>
              </div>
            </div>

            <!-- Beanstalk Logs Results -->
            <div v-if="beanstalkLogs && beanstalkLogs.length > 0">
              <div class="tw:mb-4 tw:text-sm tw:text-gray-600">
                Found {{ beanstalkLogsCount }} log{{ beanstalkLogsCount !== 1 ? 's' : '' }} for {{ loadedBeanstalkEnvironment }}
              </div>
              
              <div class="tw:space-y-3 tw:max-h-96 tw:overflow-y-auto">
                <div
                  v-for="(log, index) in beanstalkLogs"
                  :key="index"
                  class="tw:border tw:rounded-md tw:p-4">
                  <div class="tw:mb-2">
                    <div class="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
                      <span
                        :class="getLogLevelColor(log.logLevel)"
                        class="tw:inline-flex tw:items-center tw:px-2.5 tw:py-0.5 tw:rounded-full tw:text-xs tw:font-medium tw:border tw:shrink-0">
                        {{ log.logLevel || 'UNKNOWN' }}
                      </span>
                      <span class="tw:text-sm tw:text-gray-500 tw:shrink-0">
                        {{ formatTimestamp(log.timestamp) }}
                      </span>
                      <span v-if="log.userId" class="tw:text-xs tw:text-purple-600 tw:font-mono tw:bg-purple-50 tw:px-2 tw:py-1 tw:rounded tw:shrink-0">
                        {{ getUserDisplayName(log.userId) }}
                      </span>
                      <span v-if="log.correlationId" class="tw:text-xs tw:text-blue-600 tw:font-mono tw:bg-blue-50 tw:px-2 tw:py-1 tw:rounded tw:break-all tw:min-w-0">
                        {{ log.correlationId }}
                      </span>
                    </div>
                  </div>
                  <div class="tw:text-sm tw:text-gray-900 tw:font-mono tw:whitespace-pre-wrap tw:bg-gray-50 tw:p-3 tw:rounded tw:border tw:break-words tw:overflow-hidden">
                    {{ log.message }}
                  </div>
                </div>
              </div>
            </div>

            <!-- No Beanstalk Logs State -->
            <div v-else-if="selectedBeanstalkEnvironment && !beanstalkLogsLoading && !beanstalkLogsError" class="tw:text-center tw:py-8">
              <FileTextIcon class="tw:mx-auto tw:h-12 tw:w-12 tw:text-gray-400 tw:mb-4" />
              <h3 class="tw:text-lg tw:font-medium tw:text-gray-900 tw:mb-2">No Logs Found</h3>
              <p class="tw:text-gray-500">No logs found for the selected environment and filters.</p>
            </div>

            <!-- Select Environment State -->
            <div v-else-if="!selectedBeanstalkEnvironment && !beanstalkLogsLoading" class="tw:text-center tw:py-8">
              <ServerIcon class="tw:mx-auto tw:h-12 tw:w-12 tw:text-gray-400 tw:mb-4" />
              <h3 class="tw:text-lg tw:font-medium tw:text-gray-900 tw:mb-2">Select a Beanstalk Environment</h3>
              <p class="tw:text-gray-500">Choose a Beanstalk environment from the dropdown above to view its logs.</p>
            </div>
          </div>
        </div>
        </TabsContent>

        <!-- Lambda Tab Content -->
        <TabsContent value="lambda" class="tw:mt-4">
        <!-- Lambda Logs Section -->
        <div class="tw:bg-white tw:rounded-xl tw:shadow-sm tw:border tw:border-slate-200">
          <div class="tw:px-6 tw:py-4 tw:border-b tw:border-slate-200">
            <div class="tw:flex tw:items-center tw:space-x-3">
              <FileTextIcon class="tw:h-5 tw:w-5 tw:text-slate-600" />
              <h3 class="tw:text-lg tw:font-semibold tw:text-slate-900">Lambda Function Logs</h3>
            </div>
          </div>

          <div class="tw:p-6">
            <!-- Function Selection and Filters -->
            <div class="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:lg:grid-cols-3 tw:gap-4 tw:mb-6">
              <!-- Function Selection -->
              <div>
                <label class="tw:block tw:text-sm tw:font-medium tw:text-gray-700 tw:mb-2">
                  Lambda Function *
                </label>
                <select
                  v-model="selectedLambdaFunction"
                  class="tw:w-full tw:px-3 tw:py-2 tw:border tw:border-gray-300 tw:rounded-md tw:shadow-sm tw:focus:outline-none tw:focus:ring-blue-500 tw:focus:border-blue-500">
                  <option value="">Select a function</option>
                  <option v-for="func in lambdaFunctions" :key="func.name" :value="func.name">
                    {{ func.name }}
                  </option>
                </select>
              </div>

              <!-- Log Level Filter -->
              <div>
                <label class="tw:block tw:text-sm tw:font-medium tw:text-gray-700 tw:mb-2">
                  Log Level
                </label>
                <select
                  v-model="lambdaLogLevel"
                  class="tw:w-full tw:px-3 tw:py-2 tw:border tw:border-gray-300 tw:rounded-md tw:shadow-sm tw:focus:outline-none tw:focus:ring-blue-500 tw:focus:border-blue-500">
                  <option value="">All levels</option>
                  <option value="ERROR">ERROR</option>
                  <option value="WARN">WARN</option>
                  <option value="INFO">INFO</option>
                  <option value="DEBUG">DEBUG</option>
                </select>
              </div>

              <!-- Limit -->
              <div>
                <label class="tw:block tw:text-sm tw:font-medium tw:text-gray-700 tw:mb-2">
                  Limit
                </label>
                <input
                  v-model.number="lambdaLimit"
                  type="number"
                  min="1"
                  max="1000"
                  class="tw:w-full tw:px-3 tw:py-2 tw:border tw:border-gray-300 tw:rounded-md tw:shadow-sm tw:focus:outline-none tw:focus:ring-blue-500 tw:focus:border-blue-500"
                  placeholder="100" />
              </div>

              <!-- Start Time -->
              <div>
                <label class="tw:block tw:text-sm tw:font-medium tw:text-gray-700 tw:mb-2">
                  Start Time
                </label>
                <input
                  v-model="lambdaStartTime"
                  type="datetime-local"
                  class="tw:w-full tw:px-3 tw:py-2 tw:border tw:border-gray-300 tw:rounded-md tw:shadow-sm tw:focus:outline-none tw:focus:ring-blue-500 tw:focus:border-blue-500" />
              </div>

              <!-- End Time -->
              <div>
                <label class="tw:block tw:text-sm tw:font-medium tw:text-gray-700 tw:mb-2">
                  End Time
                </label>
                <input
                  v-model="lambdaEndTime"
                  type="datetime-local"
                  class="tw:w-full tw:px-3 tw:py-2 tw:border tw:border-gray-300 tw:rounded-md tw:shadow-sm tw:focus:outline-none tw:focus:ring-blue-500 tw:focus:border-blue-500" />
              </div>
            </div>

            <!-- Action Buttons -->
            <div class="tw:flex tw:gap-3 tw:mb-6">
              <Button
                @click="loadLambdaLogs"
                :disabled="!selectedLambdaFunction || lambdaLogsLoading">
                <RefreshCwIcon v-if="lambdaLogsLoading" class="tw:animate-spin tw:h-4 tw:w-4 tw:mr-2" />
                <FileTextIcon v-else class="tw:h-4 tw:w-4 tw:mr-2" />
                {{ lambdaLogsLoading ? 'Loading...' : 'Load Logs' }}
              </Button>
              
              <button
                @click="clearLambdaFilters"
                class="tw:inline-flex tw:items-center tw:px-4 tw:py-2 tw:border tw:border-slate-300 tw:text-sm tw:font-medium tw:rounded-lg tw:text-slate-700 tw:bg-white tw:hover:bg-slate-50 tw:focus:outline-none tw:focus:ring-2 tw:focus:ring-offset-2 tw:focus:ring-blue-500 tw:transition-colors tw:shadow-sm">
                <FilterIcon class="tw:h-4 tw:w-4 tw:mr-2" />
                Clear Filters
              </button>
            </div>

            <!-- Lambda Logs Error -->
            <div v-if="lambdaLogsError" class="tw:bg-red-50 tw:border tw:border-red-200 tw:rounded-md tw:p-4 tw:mb-6">
              <div class="tw:flex">
                <AlertCircleIcon class="tw:h-5 tw:w-5 tw:text-red-400 tw:mr-2" />
                <div class="tw:text-sm tw:text-red-800">
                  {{ lambdaLogsError }}
                </div>
              </div>
            </div>

            <!-- Lambda Logs Results -->
            <div v-if="lambdaLogs && lambdaLogs.length > 0">
              <div class="tw:mb-4 tw:text-sm tw:text-gray-600">
                Found {{ lambdaLogsCount }} log{{ lambdaLogsCount !== 1 ? 's' : '' }} for {{ loadedLambdaFunction }}
              </div>
              
              <div class="tw:space-y-3 tw:max-h-96 tw:overflow-y-auto">
                <div
                  v-for="(log, index) in lambdaLogs"
                  :key="index"
                  class="tw:border tw:rounded-md tw:p-4">
                  <div class="tw:mb-2">
                    <div class="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
                      <span
                        :class="getLogLevelColor(log.logLevel)"
                        class="tw:inline-flex tw:items-center tw:px-2.5 tw:py-0.5 tw:rounded-full tw:text-xs tw:font-medium tw:border tw:shrink-0">
                        {{ log.logLevel || 'UNKNOWN' }}
                      </span>
                      <span class="tw:text-sm tw:text-gray-500 tw:shrink-0">
                        {{ formatTimestamp(log.timestamp) }}
                      </span>
                    </div>
                  </div>
                  <div class="tw:text-sm tw:text-gray-900 tw:font-mono tw:whitespace-pre-wrap tw:bg-gray-50 tw:p-3 tw:rounded tw:border tw:break-words tw:overflow-hidden">
                    {{ log.message }}
                  </div>
                </div>
              </div>
            </div>

            <!-- No Lambda Logs State -->
            <div v-else-if="selectedLambdaFunction && !lambdaLogsLoading && !lambdaLogsError" class="tw:text-center tw:py-8">
              <FileTextIcon class="tw:mx-auto tw:h-12 tw:w-12 tw:text-gray-400 tw:mb-4" />
              <h3 class="tw:text-lg tw:font-medium tw:text-gray-900 tw:mb-2">No Logs Found</h3>
              <p class="tw:text-gray-500">No logs found for the selected function and filters.</p>
            </div>

            <!-- Select Lambda Function State -->
            <div v-else-if="!selectedLambdaFunction && !lambdaLogsLoading" class="tw:text-center tw:py-8">
              <ServerIcon class="tw:mx-auto tw:h-12 tw:w-12 tw:text-gray-400 tw:mb-4" />
              <h3 class="tw:text-lg tw:font-medium tw:text-gray-900 tw:mb-2">Select a Lambda Function</h3>
              <p class="tw:text-gray-500">Choose a Lambda function from the dropdown above to view its logs.</p>
            </div>
          </div>
        </div>
        </TabsContent>

        <!-- Data Loss Prevention Tab Content -->
        <TabsContent value="dlp" class="tw:mt-4">
          <div class="tw:bg-white tw:rounded-xl tw:shadow-sm tw:border tw:border-slate-200">
            <div class="tw:px-6 tw:py-4 tw:border-b tw:border-slate-200">
              <div class="tw:flex tw:items-center tw:space-x-3">
                <ShieldIcon class="tw:h-5 tw:w-5 tw:text-slate-600" />
                <h3 class="tw:text-lg tw:font-semibold tw:text-slate-900">Policy Explorer Downloads</h3>
              </div>
            </div>

            <div class="tw:p-6">
              <!-- DLP Filters -->
              <div class="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:lg:grid-cols-3 tw:gap-4 tw:mb-6">
                <!-- Start Date -->
                <div>
                  <label class="tw:block tw:text-sm tw:font-medium tw:text-gray-700 tw:mb-2">
                    Start Date
                  </label>
                  <input
                    v-model="dlpStartDate"
                    type="datetime-local"
                    class="tw:w-full tw:px-3 tw:py-2 tw:border tw:border-gray-300 tw:rounded-md tw:shadow-sm tw:focus:outline-none tw:focus:ring-blue-500 tw:focus:border-blue-500" />
                </div>

                <!-- End Date -->
                <div>
                  <label class="tw:block tw:text-sm tw:font-medium tw:text-gray-700 tw:mb-2">
                    End Date
                  </label>
                  <input
                    v-model="dlpEndDate"
                    type="datetime-local"
                    class="tw:w-full tw:px-3 tw:py-2 tw:border tw:border-gray-300 tw:rounded-md tw:shadow-sm tw:focus:outline-none tw:focus:ring-blue-500 tw:focus:border-blue-500" />
                </div>

                <!-- User -->
                <div>
                  <label class="tw:block tw:text-sm tw:font-medium tw:text-gray-700 tw:mb-2">
                    User
                  </label>
                  <Combobox
                    v-model="dlpUserId"
                    :options="userOptions"
                    placeholder="Select a user" />
                </div>
              </div>

              <!-- Action Buttons -->
              <div class="tw:flex tw:gap-3 tw:mb-6">
                <Button
                  @click="loadDlpLogs"
                  :disabled="dlpLogsLoading">
                  <RefreshCwIcon v-if="dlpLogsLoading" class="tw:animate-spin tw:h-4 tw:w-4 tw:mr-2" />
                  <ShieldIcon v-else class="tw:h-4 tw:w-4 tw:mr-2" />
                  {{ dlpLogsLoading ? 'Loading...' : 'Load Logs' }}
                </Button>

                <button
                  @click="clearDlpFilters"
                  class="tw:inline-flex tw:items-center tw:px-4 tw:py-2 tw:border tw:border-slate-300 tw:text-sm tw:font-medium tw:rounded-lg tw:text-slate-700 tw:bg-white tw:hover:bg-slate-50 tw:focus:outline-none tw:focus:ring-2 tw:focus:ring-offset-2 tw:focus:ring-blue-500 tw:transition-colors tw:shadow-sm">
                  <FilterIcon class="tw:h-4 tw:w-4 tw:mr-2" />
                  Clear Filters
                </button>
              </div>

              <!-- DLP Error -->
              <div v-if="dlpLogsError" class="tw:bg-red-50 tw:border tw:border-red-200 tw:rounded-md tw:p-4 tw:mb-6">
                <div class="tw:flex">
                  <AlertCircleIcon class="tw:h-5 tw:w-5 tw:text-red-400 tw:mr-2" />
                  <div class="tw:text-sm tw:text-red-800">
                    {{ dlpLogsError }}
                  </div>
                </div>
              </div>

              <!-- DLP Results Table -->
              <div v-if="dlpLogs.length > 0">
                <div class="tw:mb-4 tw:text-sm tw:text-gray-600">
                  Found {{ dlpLogs.length }} record{{ dlpLogs.length !== 1 ? 's' : '' }}
                </div>

                <div class="tw:overflow-x-auto tw:max-h-[32rem] tw:overflow-y-auto tw:border tw:rounded-lg">
                  <table class="tw:min-w-full tw:divide-y tw:divide-gray-200">
                    <thead class="tw:bg-gray-50 tw:sticky tw:top-0">
                      <tr>
                        <th class="tw:px-4 tw:py-3 tw:text-left tw:text-xs tw:font-medium tw:text-gray-500 tw:uppercase tw:tracking-wider">Date</th>
                        <th class="tw:px-4 tw:py-3 tw:text-left tw:text-xs tw:font-medium tw:text-gray-500 tw:uppercase tw:tracking-wider">User</th>
                        <th class="tw:px-4 tw:py-3 tw:text-left tw:text-xs tw:font-medium tw:text-gray-500 tw:uppercase tw:tracking-wider">Request URL</th>
                        <th class="tw:px-4 tw:py-3 tw:text-left tw:text-xs tw:font-medium tw:text-gray-500 tw:uppercase tw:tracking-wider">Filters</th>
                        <th class="tw:px-4 tw:py-3 tw:text-left tw:text-xs tw:font-medium tw:text-gray-500 tw:uppercase tw:tracking-wider">Columns</th>
                      </tr>
                    </thead>
                    <tbody class="tw:bg-white tw:divide-y tw:divide-gray-200">
                      <tr v-for="(log, index) in dlpLogs" :key="index" class="tw:hover:bg-gray-50">
                        <td class="tw:px-4 tw:py-3 tw:text-sm tw:text-gray-500 tw:whitespace-nowrap">
                          {{ formatTimestamp(log.createdAt) }}
                        </td>
                        <td class="tw:px-4 tw:py-3 tw:text-sm tw:whitespace-nowrap">
                          <span v-if="log.userId" class="tw:text-xs tw:text-purple-600 tw:font-mono tw:bg-purple-50 tw:px-2 tw:py-1 tw:rounded">
                            {{ getUserDisplayName(log.userId) }}
                          </span>
                          <span v-else class="tw:text-gray-400">—</span>
                        </td>
                        <td class="tw:px-4 tw:py-3 tw:text-sm tw:text-gray-900 tw:font-mono tw:break-all tw:max-w-xs">
                          {{ log.requestUrl }}
                        </td>
                        <td class="tw:px-4 tw:py-3 tw:text-sm tw:text-gray-600 tw:max-w-sm">
                          <template v-if="parseDlpFilters(log.queryParams, log.metadata).length > 0">
                            <div v-for="(filter, i) in parseDlpFilters(log.queryParams, log.metadata)" :key="i" class="tw:mb-1 tw:last:mb-0">
                              <span class="tw:font-medium tw:text-gray-700">{{ filter.label }}:</span>
                              <span class="tw:ml-1">{{ filter.value }}</span>
                            </div>
                          </template>
                          <span v-else class="tw:text-gray-400">—</span>
                        </td>
                        <td class="tw:px-4 tw:py-3 tw:text-sm tw:text-gray-600 tw:max-w-xs">
                          <template v-if="getDlpColumns(log.metadata).length > 0">
                            <span v-for="(col, i) in getDlpColumns(log.metadata)" :key="i"
                              class="tw:inline-block tw:bg-gray-100 tw:rounded tw:px-1.5 tw:py-0.5 tw:mr-1 tw:mb-1 tw:text-xs tw:font-mono">
                              {{ col }}
                            </span>
                          </template>
                          <span v-else class="tw:text-gray-500">all</span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <!-- No Results State -->
              <div v-else-if="!dlpLogsLoading && !dlpLogsError" class="tw:text-center tw:py-8">
                <ShieldIcon class="tw:mx-auto tw:h-12 tw:w-12 tw:text-gray-400 tw:mb-4" />
                <h3 class="tw:text-lg tw:font-medium tw:text-gray-900 tw:mb-2">No Audit Logs</h3>
                <p class="tw:text-gray-500">Click "Load Logs" to search for DLP audit records.</p>
              </div>
            </div>
          </div>
        </TabsContent>
      </TabsRoot>
    </div>
  </div>
</template>
